<template>
  <div class="recruits-page">
    <!-- 顶部横幅 -->
    <PageHeader
        :title="$t('recruitPage.title')"
        :subtitle="$t('recruitPage.subtitle')"
        :breadcrumbItems="[{ text: $t('recruitPage.breadcrumbHome'), path: '/' }]"
        :currentText="$t('recruitPage.breadcrumbCurrent')"
    />

    <!-- 公司文化介绍 -->
    <section class="company-culture">
      <div class="page-container">
        <div class="culture-content">
          <div class="culture-image">
            <img :src="require('@/assets/images/about/img1.jpg')" :alt="$t('recruitPage.culture.alt')" />
          </div>
          <div class="culture-text">
            <h2>{{ $t('recruitPage.culture.title') }}</h2>
            <p>{{ $t('recruitPage.culture.description') }}</p>
            <div class="culture-values">
              <div class="value-item">
                <i class="el-icon-star-on"></i>
                <h3>{{ $t('recruitPage.culture.values[0].title') }}</h3>
                <p>{{ $t('recruitPage.culture.values[0].text') }}</p>
              </div>
              <div class="value-item">
                <i class="el-icon-s-cooperation"></i>
                <h3>{{ $t('recruitPage.culture.values[1].title') }}</h3>
                <p>{{ $t('recruitPage.culture.values[1].text') }}</p>
              </div>
              <div class="value-item">
                <i class="el-icon-s-flag"></i>
                <h3>{{ $t('recruitPage.culture.values[2].title') }}</h3>
                <p>{{ $t('recruitPage.culture.values[2].text') }}</p>
              </div>
              <div class="value-item">
                <i class="el-icon-s-claim"></i>
                <h3>{{ $t('recruitPage.culture.values[3].title') }}</h3>
                <p>{{ $t('recruitPage.culture.values[3].text') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 职位导航 -->
    <section class="job-navigation">
      <div class="page-container">
        <div class="section-header text-center">
          <h2 class="section-title">{{ $t('recruitPage.category.title') }}</h2>
          <p class="section-desc">{{ $t('recruitPage.category.description') }}</p>
        </div>
        <div class="job-categories">
          <ul>
            <li v-for="type in recruitTypes" :key="type.id">
              <a href="#" :class="{ active: selectedType === type.id }" @click.prevent="selectType(type.id)">
                {{ type.name }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- 职位列表 -->
    <section class="job-listings">
      <div class="page-container">
        <div class="job-category" v-if="loading">
          <div class="loading-container text-center">
            <el-icon class="is-loading"><Loading /></el-icon>
            <p>{{ $t('recruitPage.job.loading') }}</p>
          </div>
        </div>

        <div v-else-if="recruitList.length === 0" class="no-data text-center">
          <i class="el-icon-warning-outline"></i>
          <p>{{ $t('recruitPage.job.empty') }}</p>
        </div>

        <div v-else class="job-category">
          <div class="category-header">
            <h2>{{ getTypeName(selectedType) }}</h2>
            <p>{{ $t('recruitPage.job.header') }}</p>
          </div>
          <div class="job-list">
            <div class="job-card" v-for="job in recruitList" :key="job._id">
              <div class="job-header">
                <h3 class="job-title">{{ job.position }}</h3>
                <span class="job-location">{{ job.city }}</span>
              </div>
              <div class="job-body">
                <div class="job-requirements">
                  <h4>{{ $t('recruitPage.job.requirements') }}</h4>
                  <div v-html="formatRequirement(job.requirement)"></div>
                </div>
              </div>
              <div class="job-footer">
                <span class="job-salary">{{ $t('recruitPage.job.salary') }}：{{ job.salary }}</span>
                <el-button type="primary" @click="applyJob(job)">{{ $t('recruitPage.job.apply') }}</el-button>
              </div>
            </div>
          </div>
        </div>

        <div class="pagination-container text-center" v-if="recruitList.length > 0">
          <el-pagination
              background
              layout="prev, pager, next"
              :total="total"
              :page-size="pageSize"
              :current-page="currentPage"
              @current-change="handlePageChange"
          />
        </div>
      </div>
    </section>

    <!-- 福利待遇 -->
    <section class="job-benefits">
      <div class="page-container">
        <div class="section-header text-center">
          <h2 class="section-title">{{ $t('recruitPage.benefits.title') }}</h2>
          <p class="section-desc">{{ $t('recruitPage.benefits.description') }}</p>
        </div>
        <div class="benefits-container">
          <div class="benefit-item">
            <i class="el-icon-medal"></i>
            <h3>{{ $t('recruitPage.benefits.items[0].title') }}</h3>
            <p>{{ $t('recruitPage.benefits.items[0].text') }}</p>
          </div>
          <div class="benefit-item">
            <i class="el-icon-first-aid-kit"></i>
            <h3>{{ $t('recruitPage.benefits.items[1].title') }}</h3>
            <p>{{ $t('recruitPage.benefits.items[1].text') }}</p>
          </div>
          <div class="benefit-item">
            <i class="el-icon-sunrise"></i>
            <h3>{{ $t('recruitPage.benefits.items[2].title') }}</h3>
            <p>{{ $t('recruitPage.benefits.items[2].text') }}</p>
          </div>
          <div class="benefit-item">
            <i class="el-icon-reading"></i>
            <h3>{{ $t('recruitPage.benefits.items[3].title') }}</h3>
            <p>{{ $t('recruitPage.benefits.items[3].text') }}</p>
          </div>
          <div class="benefit-item">
            <i class="el-icon-dessert"></i>
            <h3>{{ $t('recruitPage.benefits.items[4].title') }}</h3>
            <p>{{ $t('recruitPage.benefits.items[4].text') }}</p>
          </div>
          <div class="benefit-item">
            <i class="el-icon-trophy"></i>
            <h3>{{ $t('recruitPage.benefits.items[5].title') }}</h3>
            <p>{{ $t('recruitPage.benefits.items[5].text') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 申请流程 -->
    <section class="application-process">
      <div class="page-container">
        <div class="section-header text-center">
          <h2 class="section-title">{{ $t('recruitPage.process.title') }}</h2>
          <p class="section-desc">{{ $t('recruitPage.process.description') }}</p>
        </div>
        <div class="process-steps">
          <div class="process-step">
            <div class="step-number">1</div>
            <div class="step-info">
              <h3>{{ $t('recruitPage.process.steps[0].title') }}</h3>
              <p>{{ $t('recruitPage.process.steps[0].text') }}</p>
            </div>
          </div>
          <div class="process-step">
            <div class="step-number">2</div>
            <div class="step-info">
              <h3>{{ $t('recruitPage.process.steps[1].title') }}</h3>
              <p>{{ $t('recruitPage.process.steps[1].text') }}</p>
            </div>
          </div>
          <div class="process-step">
            <div class="step-number">3</div>
            <div class="step-info">
              <h3>{{ $t('recruitPage.process.steps[2].title') }}</h3>
              <p>{{ $t('recruitPage.process.steps[2].text') }}</p>
            </div>
          </div>
          <div class="process-step">
            <div class="step-number">4</div>
            <div class="step-info">
              <h3>{{ $t('recruitPage.process.steps[3].title') }}</h3>
              <p>{{ $t('recruitPage.process.steps[3].text') }}</p>
            </div>
          </div>
          <div class="process-step">
            <div class="step-number">5</div>
            <div class="step-info">
              <h3>{{ $t('recruitPage.process.steps[4].title') }}</h3>
              <p>{{ $t('recruitPage.process.steps[4].text') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系方式 -->
    <section class="contact-section">
      <div class="page-container">
        <div class="contact-info">
          <h2>{{ $t('recruitPage.contact.title') }}</h2>
          <p>{{ $t('recruitPage.contact.description') }}</p>
          <div class="contact-methods">
            <div class="contact-method">
              <i class="el-icon-message"></i>
              <span>{{ $t('recruitPage.contact.emailName') }}{{ $t('recruitPage.contact.emailDomain') }}@163.com</span>
            </div>
            <div class="contact-method">
              <i class="el-icon-phone"></i>
              <span>{{ $t('recruitPage.contact.phone', { phone: '18663127999' }) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 申请弹窗 -->
    <el-dialog v-model="dialogVisible" :title="$t('recruitPage.dialog.title')" width="500px">
      <el-form
          ref="applicationFormRef"
          :model="applicationForm"
          :rules="applicationRules"
          label-width="80px"
      >
        <el-form-item :label="$t('recruitPage.dialog.form.name')" prop="name">
          <el-input v-model="applicationForm.name" :placeholder="$t('recruitPage.dialog.form.namePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('recruitPage.dialog.form.phone')" prop="phone">
          <el-input v-model="applicationForm.phone" :placeholder="$t('recruitPage.dialog.form.phonePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('recruitPage.dialog.form.email')" prop="email">
          <el-input v-model="applicationForm.email" :placeholder="$t('recruitPage.dialog.form.emailPlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('recruitPage.dialog.form.resume')" prop="resume">
          <el-upload
              class="resume-upload"
              :auto-upload="false"
              :limit="1"
              :on-change="handleFileChange"
              :on-exceed="handleExceed"
              :on-remove="handleRemove"
              accept=".pdf,.doc,.docx"
          >
            <el-button type="primary">{{ $t('recruitPage.dialog.form.chooseFile') }}</el-button>
            <template #tip>
              <div class="el-upload__tip">{{ $t('recruitPage.dialog.form.tip') }}</div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">{{ $t('recruitPage.common.cancel') }}</el-button>
          <el-button type="primary" @click="submitApplication" :loading="submitLoading">
            {{ $t('recruitPage.common.submit') }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import PageHeader from '@/components/PageHeader.vue'
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import { useI18n } from 'vue-i18n'
import { recruitApi } from '@/api'

// 多语言支持
const { t } = useI18n()

// 招聘数据
const recruitTypes = ref([])
const recruitList = ref([])
const selectedType = ref(1) // 默认选中第一个类型
const loading = ref(false)
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(6)

// 申请表单
const dialogVisible = ref(false)
const submitLoading = ref(false)
const currentJob = ref(null)
const resumeFile = ref(null)

const applicationFormRef = ref(null)
const applicationForm = ref({
  name: '',
  phone: '',
  email: '',
  position: '',
  positionId: '',
  resume: null
})

// 表单校验规则（文本内容可做国际化扩展）
const applicationRules = {
  name: [
    { required: true, message: t('recruitPage.dialog.form.namePlaceholder'), trigger: 'blur' },
    { min: 2, max: 20, message: t('recruitPage.common.nameLengthLimit'), trigger: 'blur' }
  ],
  phone: [
    { required: true, message: t('recruitPage.dialog.form.phonePlaceholder'), trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: t('recruitPage.common.invalidPhone'), trigger: 'blur' }
  ],
  email: [
    { required: true, message: t('recruitPage.dialog.form.emailPlaceholder'), trigger: 'blur' },
    { type: 'email', message: t('recruitPage.common.invalidEmail'), trigger: 'blur' }
  ],
  resume: [
    { required: true, message: t('recruitPage.dialog.form.tip'), trigger: 'change' }
  ]
}

// 获取职位类型
const getRecruitTypes = async () => {
  try {
    const res = await recruitApi.getRecruitTypes()
    if (res.data.ActionType === 'ok') {
      recruitTypes.value = res.data.data
      getRecruitList()
    }
  } catch (err) {
    console.error(err)
    ElMessage.error(t('recruitPage.common.fetchFail'))
  }
}

// 获取职位列表
const getRecruitList = async () => {
  loading.value = true
  try {
    const res = await recruitApi.getRecruitList({
      page: currentPage.value,
      pageSize: pageSize.value,
      type: selectedType.value
    })
    if (res.data.ActionType === 'ok') {
      recruitList.value = res.data.data
      total.value = res.data.total
    }
  } catch (err) {
    console.error(err)
    ElMessage.error(t('recruitPage.common.fetchFail'))
    recruitList.value = []
  } finally {
    loading.value = false
  }
}

// 选中类型
const selectType = (typeId) => {
  selectedType.value = typeId
  currentPage.value = 1
  getRecruitList()
}

// 翻页
const handlePageChange = (page) => {
  currentPage.value = page
  getRecruitList()
}

// 获取类型名称
const getTypeName = (typeId) => {
  const type = recruitTypes.value.find(t => t.id === typeId)
  return type ? type.name : t('recruitPage.common.unknown')
}

// 格式化职位要求
const formatRequirement = (requirement) => {
  if (!requirement) return `<p>${t('recruitPage.common.noRequirement')}</p>`
  const lines = requirement.split('\n').map(l => l.trim()).filter(Boolean)
  if (lines.length === 0) return `<p>${t('recruitPage.common.noRequirement')}</p>`
  return `<ul>${lines.map(line => `<li>${line}</li>`).join('')}</ul>`
}

// 打开申请弹窗
const applyJob = (job) => {
  currentJob.value = job
  applicationForm.value = {
    name: '',
    phone: '',
    email: '',
    position: job.position,
    positionId: job._id,
    resume: null
  }
  resumeFile.value = null
  dialogVisible.value = true
}

// 上传文件
const handleFileChange = (file) => {
  const acceptedTypes = ['.pdf', '.doc', '.docx']
  const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
  if (!acceptedTypes.includes(ext)) {
    ElMessage.error(t('recruitPage.common.invalidFileType'))
    return false
  }
  if (file.size > 5 * 1024 * 1024) {
    ElMessage.error(t('recruitPage.common.fileTooLarge'))
    return false
  }
  resumeFile.value = file.raw
  applicationForm.value.resume = file.raw
  return true
}

const handleExceed = () => {
  ElMessage.warning(t('recruitPage.common.exceedFileLimit'))
}

const handleRemove = () => {
  resumeFile.value = null
  applicationForm.value.resume = null
}

// 提交申请
const submitApplication = () => {
  applicationFormRef.value.validate(async (valid) => {
    if (!valid) return
    submitLoading.value = true
    try {
      const formData = new FormData()
      for (const key in applicationForm.value) {
        if (key !== 'resume') {
          formData.append(key, applicationForm.value[key])
        }
      }
      formData.append('resume', resumeFile.value)

      const res = await recruitApi.submitJobApplication(formData)
      if (res.data.ActionType === 'ok') {
        ElMessage.success(t('recruitPage.common.submitSuccess'))
        dialogVisible.value = false
      } else {
        ElMessage.error(res.data.message || t('recruitPage.common.submitFail'))
      }
    } catch (err) {
      console.error(err)
      ElMessage.error(t('recruitPage.common.submitFail'))
    } finally {
      submitLoading.value = false
    }
  })
}

// 初始化
onMounted(() => {
  getRecruitTypes()
})
</script>

<style lang="scss" scoped>
.recruits-page {
  padding-top: 100px; /* 根据导航栏高度调整 */

  padding-bottom: 60px;

    .company-culture {
        padding: 80px 0;
        background-color: #fff;

        .culture-content {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 50px;
            align-items: center;

            @media (max-width: 992px) {
                grid-template-columns: 1fr;
            }

            .culture-image {
                height: 400px;
                border-radius: 10px;
                overflow: hidden;
                box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
            }

            .culture-text {
                h2 {
                    font-size: 32px;
                    font-weight: 700;
                    color: #333;
                    margin-bottom: 20px;
                }

                p {
                    font-size: 16px;
                    color: #666;
                    line-height: 1.7;
                    margin-bottom: 30px;
                }

                .culture-values {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 20px;

                    .value-item {
                        padding: 20px;
                        background-color: #f9f9f9;
                        border-radius: 8px;
                        transition: transform 0.3s ease;

                        &:hover {
                            transform: translateY(-5px);
                            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
                        }

                        i {
                            font-size: 30px;
                            color: #409EFF;
                            margin-bottom: 10px;
                        }

                        h3 {
                            font-size: 18px;
                            font-weight: 600;
                            color: #333;
                            margin-bottom: 10px;
                        }

                        p {
                            font-size: 14px;
                            color: #666;
                            margin-bottom: 0;
                        }
                    }
                }
            }
        }
    }

    .job-navigation {
        padding: 50px 0 30px;
        background-color: #f5f7fa;

        .job-categories {
            margin-top: 30px;

            ul {
                display: flex;
                justify-content: center;
                flex-wrap: wrap;
                gap: 20px;
                list-style: none;
                padding: 0;

                li {
                    a {
                        display: block;
                        padding: 10px 20px;
                        background-color: #fff;
                        color: #333;
                        text-decoration: none;
                        border-radius: 4px;
                        font-weight: 500;
                        transition: all 0.3s ease;
                        border: 1px solid #e4e7ed;

                        &:hover {
                            color: #409EFF;
                            border-color: #409EFF;
                        }

                        &.active {
                            background-color: #409EFF;
                            color: #fff;
                            border-color: #409EFF;
                        }
                    }
                }
            }
        }
    }

    .job-listings {
        padding: 50px 0;

        .job-category {
            margin-bottom: 60px;

            .category-header {
                margin-bottom: 30px;
                border-left: 4px solid #409EFF;
                padding-left: 15px;

                h2 {
                    font-size: 24px;
                    font-weight: 600;
                    color: #333;
                    margin-bottom: 10px;
                }

                p {
                    color: #666;
                    font-size: 14px;
                }
            }

            .job-list {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 30px;

                @media (max-width: 992px) {
                    grid-template-columns: 1fr;
                }

                .job-card {
                    background-color: #fff;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                    transition: transform 0.3s ease;

                    &:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
                    }

                    .job-header {
                        padding: 20px;
                        border-bottom: 1px solid #f1f1f1;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;

                        .job-title {
                            font-size: 18px;
                            font-weight: 600;
                            color: #333;
                            margin: 0;
                        }

                        .job-location {
                            background-color: #ecf5ff;
                            color: #409EFF;
                            padding: 5px 10px;
                            border-radius: 4px;
                            font-size: 12px;
                        }
                    }

                    .job-body {
                        padding: 20px;

                        .job-requirements, .job-responsibilities {
                            margin-bottom: 20px;

                            h4 {
                                font-size: 16px;
                                font-weight: 600;
                                color: #333;
                                margin-bottom: 10px;
                            }

                            ul {
                                padding-left: 20px;
                                color: #666;
                                font-size: 14px;
                                line-height: 1.7;
                                margin-bottom: 0;

                                li {
                                    margin-bottom: 5px;
                                }
                            }
                        }
                    }

                    .job-footer {
                        padding: 15px 20px;
                        background-color: #f9f9f9;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;

                        .job-salary {
                            color: #ff6b6b;
                            font-weight: 500;
                        }
                    }
                }
            }
        }
    }

    .job-benefits {
        padding: 80px 0;
        background-color: #f5f7fa;

        .benefits-container {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 30px;
            margin-top: 50px;

            @media (max-width: 992px) {
                grid-template-columns: repeat(2, 1fr);
            }

            @media (max-width: 576px) {
                grid-template-columns: 1fr;
            }

            .benefit-item {
                background-color: #fff;
                padding: 30px;
                border-radius: 8px;
                text-align: center;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                transition: transform 0.3s ease;

                &:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
                }

                i {
                    font-size: 36px;
                    color: #409EFF;
                    margin-bottom: 15px;
                    display: block;
                }

                h3 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #333;
                    margin-bottom: 10px;
                }

                p {
                    color: #666;
                    font-size: 14px;
                    line-height: 1.6;
                }
            }
        }
    }

    .application-process {
        padding: 80px 0;

        .process-steps {
            margin-top: 50px;
            display: flex;
            justify-content: space-between;
            position: relative;

            &:before {
                content: '';
                position: absolute;
                top: 40px;
                left: 70px;
                right: 70px;
                height: 3px;
                background-color: #e4e7ed;
                z-index: 0;
            }

            @media (max-width: 992px) {
                flex-direction: column;
                gap: 30px;

                &:before {
                    display: none;
                }
            }

            .process-step {
                display: flex;
                flex-direction: column;
                align-items: center;
                position: relative;
                z-index: 1;
                flex: 1;

                @media (max-width: 992px) {
                    flex-direction: row;
                    gap: 20px;
                }

                .step-number {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    background-color: #409EFF;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 28px;
                    font-weight: 700;
                    margin-bottom: 20px;
                    box-shadow: 0 5px 15px rgba(64, 158, 255, 0.3);
                }

                .step-info {
                    text-align: center;

                    @media (max-width: 992px) {
                        text-align: left;
                    }

                    h3 {
                        font-size: 18px;
                        font-weight: 600;
                        color: #333;
                        margin-bottom: 5px;
                    }

                    p {
                        color: #666;
                        font-size: 14px;
                    }
                }
            }
        }
    }

    .contact-section {
        padding: 60px 0;
        background-color: #f5f7fa;

        .contact-info {
            max-width: 600px;
            margin: 0 auto;
            text-align: center;

            h2 {
                font-size: 28px;
                font-weight: 600;
                color: #333;
                margin-bottom: 15px;
            }

            p {
                color: #666;
                font-size: 16px;
                margin-bottom: 30px;
            }

            .contact-methods {
                display: flex;
                flex-direction: column;
                gap: 15px;

                .contact-method {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;

                    i {
                        font-size: 20px;
                        color: #409EFF;
                    }

                    span {
                        font-size: 16px;
                        color: #333;
                    }
                }
            }
        }
    }
}

.section-header {
    margin-bottom: 30px;

    .section-title {
        font-size: 32px;
        font-weight: 700;
        color: #333;
        margin-bottom: 15px;
    }

    .section-desc {
        font-size: 16px;
        color: #666;
        max-width: 700px;
        margin: 0 auto;
    }

    &.text-center {
        text-align: center;
    }
}

.page-container {
    max-width: 1200px;
    padding: 0 15px;
    margin: 0 auto;
}

.loading-container {
    padding: 40px 0;

    .el-icon {
        font-size: 36px;
        color: #409EFF;
    }

    p {
        margin-top: 20px;
        color: #666;
    }
}

.no-data {
    padding: 60px 0;

    i {
        font-size: 48px;
        color: #ccc;
        margin-bottom: 15px;
    }

    p {
        color: #888;
        font-size: 16px;
    }
}

.pagination-container {
    margin-top: 40px;
}

.text-center {
    text-align: center;
}

.resume-upload {
  width: 100%;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

:deep(.el-upload-list) {
  margin-top: 10px;
}
</style>
